import { FileDropzone } from '@monorepo/shared/componentsV2/FileDropzone';
import { Loading } from '@monorepo/shared/componentsV2/Loading';
import { FileData } from '@monorepo/shared/hooks/useFileInputHandler';
import TrashIcon from '@svg/trash.svg';
import {
  EDP_AI_FILE_SIZE_LIMIT_IN_MB,
  EDP_AI_SUPPORTED_FILE_MIME_TYPES,
  EDP_AI_SUPPORTED_FILE_TYPES_LABELS,
} from 'mapistry-shared';
import React, { useCallback, useMemo } from 'react';
import { FileUploadStatus, type UploadedFile } from '../hooks/useUploadedFiles';
import { LogEntriesErrorOverlay } from '../LogEntriesErrorOverlay';
import {
  DeleteButton,
  FileListContainerHeader,
  FileListContainerHeaderTitle,
  FileListScrollContainer,
  FileName,
  FileNameAndStatusContainer,
  FileStatus,
  ModalContent,
  OutlinedFileContainer,
  StatusIndicator,
  StyledCheckIcon,
  StyledErrorIcon,
  SubContainer,
  SubContainerTitle,
} from '../styled';

const statusLabels: Record<FileUploadStatus, string> = {
  [FileUploadStatus.Processing]: 'Analyzing...',
  [FileUploadStatus.Ready]: 'Completed',
  [FileUploadStatus.Completed]: 'Completed',
  [FileUploadStatus.Error]: 'Error',
};

function getStatusLabel(status: FileUploadStatus): string {
  return statusLabels[status];
}

function FileStatusIcon({ status }: { status: FileUploadStatus }) {
  switch (status) {
    case FileUploadStatus.Processing:
      return <Loading size="small" />;
    case FileUploadStatus.Error:
      return <StyledErrorIcon />;
    case FileUploadStatus.Ready:
    case FileUploadStatus.Completed:
    default:
      return <StyledCheckIcon />;
  }
}

type LogEntriesFileUploadInputModalPageProps = {
  uploadedFiles: Map<string, UploadedFile>;
  addFiles: (files: File[]) => FileData[] | undefined;
  handleUploadLogEntriesImage: (fileData: FileData[]) => void;
  removeFileData: (requestId: string) => void;
  showErrorOverlay: boolean;
  areAllFilesCompletedOrHaveErrors: boolean;
  setShowErrorOverlay: React.Dispatch<React.SetStateAction<boolean>>;
};

export function LogEntriesFileUploadInputModalPage({
  uploadedFiles,
  addFiles,
  handleUploadLogEntriesImage,
  removeFileData,
  showErrorOverlay,
  areAllFilesCompletedOrHaveErrors,
  setShowErrorOverlay,
}: LogEntriesFileUploadInputModalPageProps) {
  const handleFilesSelected = useCallback(
    (files: File[]) => {
      const newFileData = addFiles(files);
      if (newFileData) {
        handleUploadLogEntriesImage(newFileData);
      }
    },
    [addFiles, handleUploadLogEntriesImage],
  );

  const numberOfFilesUploading = useMemo(
    () =>
      Array.from(uploadedFiles.values()).filter(
        (file) => file.status === FileUploadStatus.Processing,
      ).length,
    [uploadedFiles],
  );

  if (areAllFilesCompletedOrHaveErrors && showErrorOverlay) {
    return (
      <LogEntriesErrorOverlay
        uploadedFiles={uploadedFiles}
        removeFileData={removeFileData}
        setShowErrorOverlay={setShowErrorOverlay}
      />
    );
  }

  return (
    <ModalContent>
      <SubContainer>
        <SubContainerTitle>Image Upload</SubContainerTitle>
        <FileDropzone
          inputLabel="Upload your files to get started"
          buttonText="Upload files"
          acceptedMimeTypes={EDP_AI_SUPPORTED_FILE_MIME_TYPES}
          onFilesSelected={handleFilesSelected}
          supportedFileTypeLabels={EDP_AI_SUPPORTED_FILE_TYPES_LABELS}
          fileSizeLimitInMb={EDP_AI_FILE_SIZE_LIMIT_IN_MB}
        />
        {Array.from(uploadedFiles.values()).length > 0 && (
          <FileListContainerHeader>
            <FileListContainerHeaderTitle>
              {numberOfFilesUploading > 0
                ? `${numberOfFilesUploading} ${
                    numberOfFilesUploading === 1 ? 'file' : 'files'
                  } uploading...`
                : 'Upload Complete'}
            </FileListContainerHeaderTitle>
          </FileListContainerHeader>
        )}
        <FileListScrollContainer>
          {Array.from(uploadedFiles.entries()).map(
            ([requestId, uploadedFile]) => {
              const { originFile, status } = uploadedFile;

              return (
                <OutlinedFileContainer key={requestId}>
                  <StatusIndicator>
                    <FileStatusIcon status={status} />
                  </StatusIndicator>
                  <FileNameAndStatusContainer>
                    <FileName>{originFile.name}</FileName>
                    <FileStatus>{getStatusLabel(status)}</FileStatus>
                  </FileNameAndStatusContainer>
                  <DeleteButton
                    aria-label={`Remove ${originFile.name}`}
                    onClick={() => removeFileData(requestId)}
                  >
                    <TrashIcon />
                  </DeleteButton>
                </OutlinedFileContainer>
              );
            },
          )}
        </FileListScrollContainer>
      </SubContainer>
    </ModalContent>
  );
}
